import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { formatDateOnly, getFormatConfig, initializeFormats } from '../../../utils/global';
import { fetchWorkforceBusinessUnits } from '../WorkforceOperations/workforceBusinessUnitUtils';
import GlobalEmployeeImportDialog from './globalEmployeeImport';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonatePageSearchFieldSx,
  harmonatePageSearchIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const formatHourlyMoney = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const yesNo = (value) => {
  if (value === true) return 'YES';
  if (value === false) return 'NO';
  return '';
};

const mapEmployeeRow = (employee, businessUnitNameById) => {
  const propertyId = employee.primaryPropertyId || '';
  return {
    id: employee.employeeId,
    employeeCode: employee.employeeCode || '',
    firstName: employee.firstName || '',
    lastName: employee.lastName || '',
    fullName: employee.fullName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
    phone: employee.phone || '',
    homePhone: employee.homePhone || '',
    hireDate: employee.hireDate || '',
    employmentType: employee.employmentType || '',
    status: employee.status || '',
    hourlyRate: employee.hourlyRate ?? '',
    hourlyRateAdjustment: employee.hourlyRateAdjustment ?? '',
    annualSalary: employee.annualSalary ?? '',
    isSalaried: employee.isSalaried === true,
    isOvertimeAllowed: employee.isOvertimeAllowed,
    department: employee.department || '',
    departmentCode: employee.departmentCode || employee.department_code || '',
    jobcodeLevel1: employee.jobcodeLevel1 || '',
    jobcodeLevel2: employee.jobcodeLevel2 || '',
    businessUnitId: propertyId,
    businessUnitName: businessUnitNameById[propertyId] || propertyId || '',
  };
};

const DetailItem = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ display: 'block', color: ht.text }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', color: ht.text }}>
      {value === null || value === undefined || value === '' ? '—' : String(value)}
    </Typography>
  </Box>
);

const GlobalEmployee = () => {
  const { canUpload } = useFunctionPermissions(FC.GLOBAL_EMPLOYEE);
  const tenantId = getTenantId();
  const username = localStorage.getItem('username') || '';
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formatConfig, setFormatConfig] = useState(getFormatConfig());
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    initializeFormats(tenantId).then(() => setFormatConfig(getFormatConfig()));
  }, [tenantId]);

  const fetchEmployees = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [employees, units, departments] = await Promise.all([
        apiFetch(`conductor/employees/by-tenant-all?tenant_id=${encodeURIComponent(tenantId)}`),
        fetchWorkforceBusinessUnits(tenantId).catch(() => []),
        apiFetch(`conductor/department-masters/tenant/${encodeURIComponent(tenantId)}`).catch(() => []),
      ]);
      const businessUnitNameById = {};
      (Array.isArray(units) ? units : []).forEach((unit) => {
        const id = unit.propertyId || unit.businessUnitId || unit.id;
        if (!id) return;
        businessUnitNameById[id] =
          unit.businessUnitName || unit.propertyName || unit.PROPERTY_NAME || String(id);
      });

      const departmentCodeByName = {};
      (Array.isArray(departments) ? departments : []).forEach((dept) => {
        const name = String(dept.departmentName || dept.department_name || '').trim().toLowerCase();
        const code = String(dept.departmentCode || dept.department_code || '').trim();
        if (name && code) departmentCodeByName[name] = code;
      });

      setRows(
        (Array.isArray(employees) ? employees : []).map((employee) => {
          const row = mapEmployeeRow(employee, businessUnitNameById);
          if (!row.departmentCode && row.department) {
            row.departmentCode = departmentCodeByName[row.department.trim().toLowerCase()] || '';
          }
          return row;
        })
      );
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [tenantId, showError]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.employeeCode,
        row.firstName,
        row.lastName,
        row.fullName,
        row.businessUnitName,
        row.phone,
        row.homePhone,
        row.employmentType,
        row.status,
        row.department,
        row.departmentCode,
        row.jobcodeLevel1,
        row.jobcodeLevel2,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search]);

  const columns = useMemo(
    () => [
      { field: 'employeeCode', headerName: 'Employee Code', width: 140, resizable: true },
      { field: 'firstName', headerName: 'First Name', width: 120, resizable: true },
      { field: 'lastName', headerName: 'Last Name', width: 120, resizable: true },
      { field: 'businessUnitName', headerName: 'Business Unit', width: 180, resizable: true },
      { field: 'phone', headerName: 'Mobile Phone', width: 140, resizable: true },
      { field: 'homePhone', headerName: 'Home Phone', width: 140, resizable: true },
      {
        field: 'hireDate',
        headerName: 'Hire Date',
        width: 120,
        resizable: true,
        valueFormatter: (params) =>
          params.value ? formatDateOnly(params.value, formatConfig.DateFormat) : '',
      },
      { field: 'employmentType', headerName: 'Employment Type', width: 150, resizable: true },
      { field: 'status', headerName: 'Status', width: 110, resizable: true },
      {
        field: 'hourlyRate',
        headerName: 'Hourly Rate',
        width: 120,
        resizable: true,
        valueFormatter: (params) => formatHourlyMoney(params.value),
      },
      { field: 'department', headerName: 'Department', width: 140, resizable: true },
      { field: 'jobcodeLevel1', headerName: 'Jobcode Level 1', width: 140, resizable: true },
      { field: 'jobcodeLevel2', headerName: 'Jobcode Level 2', width: 140, resizable: true },
    ],
    [formatConfig.DateFormat]
  );

  const handleRowClick = (params) => {
    setSelectedEmployee(params.row);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedEmployee(null);
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Global Employee
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search employees…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: { xs: 180, sm: 280 }, ...harmonatePageSearchFieldSx }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={harmonatePageSearchIconSx} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={canUpload ? 'Import from Excel or CSV' : 'No upload permission'}>
            <span>
              <IconButton
                sx={harmonatePlainIconButtonSx}
                disabled={!canUpload}
                onClick={() => {
                  if (!canUpload) return;
                  setImportOpen(true);
                }}
              >
                <UploadFileIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          rowHeight={40}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          sx={{
            ...harmonateDataGridSx,
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab },
          }}
        />
      </Paper>

      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 700, color: ht.white }}>
            Employee details
          </Typography>
          <IconButton onClick={handleCloseDetails} size="small" aria-label="close" sx={harmonateDialogCloseIconSx}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          {selectedEmployee ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
              }}
            >
              <DetailItem label="Employee Code" value={selectedEmployee.employeeCode} />
              <DetailItem label="Business Unit" value={selectedEmployee.businessUnitName} />
              <DetailItem label="First Name" value={selectedEmployee.firstName} />
              <DetailItem label="Last Name" value={selectedEmployee.lastName} />
              <DetailItem label="Mobile Phone" value={selectedEmployee.phone} />
              <DetailItem label="Home Phone" value={selectedEmployee.homePhone} />
              <DetailItem
                label="Hire Date"
                value={
                  selectedEmployee.hireDate
                    ? formatDateOnly(selectedEmployee.hireDate, formatConfig.DateFormat)
                    : ''
                }
              />
              <DetailItem label="Employment Type" value={selectedEmployee.employmentType} />
              <DetailItem label="Status" value={selectedEmployee.status} />
              <DetailItem label="Department" value={selectedEmployee.department} />
              <DetailItem label="Department Code" value={selectedEmployee.departmentCode} />
              <DetailItem label="Is Salaried" value={yesNo(selectedEmployee.isSalaried)} />
              {selectedEmployee.isSalaried ? (
                <DetailItem
                  label="Annual Salary"
                  value={formatHourlyMoney(selectedEmployee.annualSalary)}
                />
              ) : null}
              <DetailItem
                label="Hourly Rate"
                value={
                  selectedEmployee.isSalaried
                    ? formatHourlyMoney(0)
                    : formatHourlyMoney(selectedEmployee.hourlyRate)
                }
              />
              <DetailItem
                label="Is Overtime Allowed"
                value={yesNo(selectedEmployee.isOvertimeAllowed)}
              />
              <DetailItem label="Jobcode Level 1" value={selectedEmployee.jobcodeLevel1} />
              <DetailItem label="Jobcode Level 2" value={selectedEmployee.jobcodeLevel2} />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDetails} variant="outlined" sx={harmonateCancelButtonSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <GlobalEmployeeImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        tenantId={tenantId}
        username={username}
        showSuccess={showSuccess}
        showError={showError}
        onImportSuccess={fetchEmployees}
      />

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default GlobalEmployee;
